import React, { useEffect, useState } from 'react';
import { isTablet } from 'react-device-detect';
import { UltrarubigramStore } from './UltrarubigramStore';
import { onReveal, saveUltrarubigram } from './ultrarubigramUtils';
import Board from './components/Board';
import Instructions from './components/Instructions';
import PuzzleTopBar from '../shared/PuzzleTopBar';
import PauseCover from '../shared/PauseCover';
import { PuzzleNames } from '../../../utils/types';
import { useAmplitudeTracking, useFirstTimeInstructions, usePageVisibilityPause, useStructuredDataEmbedding } from '../shared/utils';
import { usePuzzleTracking } from '../../../utils/usePuzzleTracking';
import './Ultrarubigram.less';

/** Below this the puzzle can't be laid out legibly; it is a desktop puzzle. */
const MIN_WINDOW_WIDTH = 900;
const MIN_WINDOW_HEIGHT = 560;

/** Vertical space the puzzle can't use: site header + puzzle top bar, outer buttons, margins. */
const RESERVED_HEIGHT = 105 + 2 * 52 + 24;
const RESERVED_WIDTH = 2 * 52 + 40;
/** Borders: 24 cells plus the lines between and around them (see Ultrarubigram.less). */
const BORDER_WIDTH = 47;
const MIN_CELL = 16;
const MAX_CELL = 38;

/** One cell's size in whole pixels: as large as the window allows, so the 24x24 fits with room for the controls. */
const fitCellSize = () => {
  const byHeight = Math.floor((window.innerHeight - RESERVED_HEIGHT - BORDER_WIDTH) / 24);
  const byWidth = Math.floor((window.innerWidth - RESERVED_WIDTH - BORDER_WIDTH) / 24);
  return Math.max(MIN_CELL, Math.min(MAX_CELL, byHeight, byWidth));
};

const useWindowFit = () => {
  const measure = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      cell: fitCellSize(),
      tooSmall: width < MIN_WINDOW_WIDTH || height < MIN_WINDOW_HEIGHT,
      // A portrait tablet tall enough that, turned on its side, the window would pass.
      suggestLandscape: isTablet && height > width && height >= MIN_WINDOW_WIDTH && width >= MIN_WINDOW_HEIGHT,
    };
  };
  const [fit, setFit] = useState(measure);

  useEffect(() => {
    const onResize = () => setFit(measure());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return fit;
};

const Ultrarubigram = () => {
  const { solved, revealed, isRevealing } = UltrarubigramStore.useState(s => ({
    solved: s.solved, revealed: s.revealed, isRevealing: s.isRevealing,
  }));
  const { cell, tooSmall, suggestLandscape } = useWindowFit();

  useFirstTimeInstructions(PuzzleNames.Ultrarubigram, UltrarubigramStore as any);
  usePageVisibilityPause(UltrarubigramStore as any, saveUltrarubigram);
  useStructuredDataEmbedding(PuzzleNames.Ultrarubigram);
  useAmplitudeTracking(PuzzleNames.Ultrarubigram);
  usePuzzleTracking(PuzzleNames.Ultrarubigram);

  const containerClasses = 'puzzleContainer ultrarubigramContainer'
    + (solved ? ' solved' : '')
    + (revealed && !isRevealing ? ' revealed' : '');

  // Whole, even pixel sizes so the two halves of a shared flip tab rasterize identically.
  const containerStyle = {
    '--cell': `${cell}px`,
    '--tab': `${2 * Math.round(cell * 0.36)}px`,
  } as React.CSSProperties;

  return (
    <>
      <PuzzleTopBar
        puzzleName={PuzzleNames.Ultrarubigram}
        store={UltrarubigramStore as any}
        onReveal={onReveal}
        onSave={saveUltrarubigram}
        InstructionsComponent={Instructions}
      />
      <div className={containerClasses} style={containerStyle}>
        {tooSmall ?
          <div className="desktopOnlyNotice">
            <div>The Ultrarubigram needs a window at least {MIN_WINDOW_WIDTH} pixels wide and {MIN_WINDOW_HEIGHT} tall.</div>
            {suggestLandscape && <div>Try turning your tablet to landscape.</div>}
          </div>
          :
          <Board/>
        }
        {revealed && !isRevealing &&
          <div className="outcomeBanner">
            <div>Solution revealed. Try again next time!</div>
            <div>New Ultrarubigrams on the first of each month.</div>
          </div>
        }
        {solved &&
          <div className="outcomeBanner">
            Solved! New Ultrarubigrams on the first of each month.
          </div>
        }
        <PauseCover store={UltrarubigramStore as any}/>
      </div>
    </>
  );
};

export default Ultrarubigram;
